import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { Card } from '@leanin/ui';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { SignupForm } from '@/components/auth/SignupForm';
import { EntryJourneyScreen } from '@/components/entry-journeys';
import { redirectIfSignedIn } from '@/lib/use-session';
import {
  isValidReferralCode,
  storeSignupRef,
} from '@/lib/signup-ref-cookie';
import { returnPathSearchParam } from '@/lib/return-path';
import {
  isEntryJourney,
  readEntryJourney,
  writeEntryJourney,
  type EntryJourney,
} from '@/lib/entry-journey';

type SignupSearch = {
  // Clerk-issued ticket on invited signups. The Clerk Backend API returns a
  // URL of the form `${redirectUrl}?__clerk_ticket=…` when an invitation is
  // created with `notify=false`; landing here with that param means the
  // user is accepting a directory or admin invite.
  __clerk_ticket?: string;
  // Group-invite token forwarded from `/invites/:token` when a signed-out
  // visitor lands on a redeem URL. Stashed into Clerk's `unsafeMetadata`
  // during `signUp.create()` so the server's `user.created` webhook can
  // auto-redeem it after Clerk provisions the account.
  pendingInviteToken?: string;
  // Referral tracking code. Attributes the resulting signup to an admin's
  // referral link; grants nothing. Stashed into a first-party cookie on
  // arrival so it survives the OAuth round-trip, then claimed at /auth/finishing.
  ref?: string;
  // Ad entry journey forwarded from the public entry pages. Grants nothing —
  // it only shapes funnel copy and the onboarding persona preset. Stashed
  // into sessionStorage on arrival (same mechanism as the entry pages
  // themselves) so it survives the Clerk round-trips, then cleared when
  // onboarding finishes or an already-onboarded user lands at /auth/finishing.
  journey?: EntryJourney;
  // In-app page to land on once onboarding finishes. Set when Clerk's OAuth
  // callback sends a Google sign-up here (transfer / missing requirements)
  // from a join that carried `?next=`; forwarded to the signup form.
  next?: string;
};

export const Route = createFileRoute('/auth/signup')({
  beforeLoad: ({ context, search }) => redirectIfSignedIn(context, search.next),
  validateSearch: (search: Record<string, unknown>): SignupSearch => {
    const ticket = search['__clerk_ticket'];
    const pendingInviteToken = search['pendingInviteToken'];
    const ref = search['ref'];
    const journey = search['journey'];
    const next = returnPathSearchParam.parse(search['next']);
    return {
      ...(typeof ticket === 'string' && ticket.length > 0
        ? { __clerk_ticket: ticket }
        : {}),
      ...(typeof pendingInviteToken === 'string' && pendingInviteToken.length > 0
        ? { pendingInviteToken }
        : {}),
      ...(typeof ref === 'string' && isValidReferralCode(ref) ? { ref } : {}),
      ...(typeof journey === 'string' && isEntryJourney(journey)
        ? { journey }
        : {}),
      ...(next !== undefined ? { next } : {}),
    };
  },
  component: SignupPage,
});

function SignupPage() {
  const t = useTranslations('Auth');
  const { __clerk_ticket: ticket, pendingInviteToken } = Route.useSearch();
  const { ref, journey, next } = Route.useSearch();
  // A journey stashed by an entry page on a previous navigation in this tab.
  // Read once at mount — the effect below only ever writes the same value
  // back, so the stored copy can't drift from this snapshot mid-render.
  const [storedJourney] = useState(readEntryJourney);
  // Persist the referral code and entry journey before the OAuth round-trip
  // leaves this origin. Validated in validateSearch (ref is re-checked
  // inside storeSignupRef).
  useEffect(() => {
    if (ref !== undefined) storeSignupRef(ref);
  }, [ref]);
  useEffect(() => {
    if (journey !== undefined) writeEntryJourney(journey);
  }, [journey]);
  const activeJourney = journey ?? storedJourney;
  const form = (
    <SignupForm
      {...(ticket !== undefined ? { invitationTicket: ticket } : {})}
      {...(pendingInviteToken !== undefined
        ? { pendingInviteToken }
        : {})}
      {...(activeJourney !== null ? { journey: activeJourney } : {})}
      {...(next !== undefined ? { next } : {})}
    />
  );
  // Ad journeys swap the promo split-screen for the journey's own chrome:
  // wordmark + funnel stepper over a centered form card. The email-code
  // verification phase lives inside SignupForm and stays on "Sign up" — the
  // stepper only advances when onboarding begins. Organic and invited
  // signups keep the standard AuthLayout untouched.
  if (activeJourney !== null) {
    return (
      <EntryJourneyScreen journey={activeJourney} current="sign-up">
        <Card style={{ width: '100%', maxWidth: 440 }}>{form}</Card>
      </EntryJourneyScreen>
    );
  }
  return <AuthLayout headline={t('layout.promoHeadline')}>{form}</AuthLayout>;
}
